/* clickable-corner-contact -- launcher/panel behaviour + AJAX submit.

   PATHWAY (proposed + built, 11 Aug round 1): the panel posts the SAME
   native Shopify contact form the contact page uses (form_type=contact
   -> store email -> Gorgias ingests into tickets). No API credentials
   in theme code; ticket flow identical to the main form, subject marker
   distinguishes the surface.

   FAIL CLOSED at every layer:
   - the launcher is a real <a href="/pages/contact"> decorated into a
     panel opener -- no JS (or this file broken) means the click simply
     navigates to the contact page, never a dead click;
   - the submit obtains the hCaptcha token IN the AJAX path
     (hcaptcha.execute on the widget Shopify mounts in this form;
     challenged customers solve the popup, pinned bottom-left, and the
     message still delivers) -- see the inline note: the approved
     native-submit fallback was server-dead (bare 400 on token-less
     POSTs, no challenge flow) and was replaced same-day, flagged;
   - any submit error (missing token, network, non-OK) paints the
     error state with a contact-page link -- the message stays in the
     form, never silently lost.

   Modal basics: focus moves to the first field on open; Escape and an
   outside click close; focus returns to the launcher.

   Loaded by snippets/clickable-corner-contact.liquid (full bundle
   travels with the component, rule 21; run-once guarded). */
(function () {
  'use strict';
  if (window.__ckCornerContactLoaded) return;
  window.__ckCornerContactLoaded = true;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    var root = document.querySelector('[data-ck-corner]');
    if (!root) return;
    var launch = root.querySelector('[data-ck-corner-launch]');
    var panel = root.querySelector('[data-ck-corner-panel]');
    var backdrop = root.querySelector('[data-ck-corner-backdrop]');
    var closeBtn = root.querySelector('[data-ck-corner-close]');
    var form = root.querySelector('form');
    var sent = root.querySelector('[data-ck-corner-sent]');
    var errBox = root.querySelector('[data-ck-corner-error]');
    if (!launch || !panel || !form) return;

    /* decorate: only once JS is live does the link become an opener */
    launch.setAttribute('role', 'button');
    launch.setAttribute('aria-expanded', 'false');
    launch.setAttribute('aria-haspopup', 'dialog');

    /* Visual-viewport centring (Lucas, 20 Aug): while the panel is open
       the root publishes --ck-vv-h/--ck-vv-top so the <=767 CSS centres
       the panel in the VISUAL viewport — the keyboard opening/closing
       re-centres it (instant set, no transition on `top`: no jump).
       Fallback when unsupported/closed: the CSS's own 100svh. */
    var vv = window.visualViewport || null;
    function syncViewportVars() {
      if (!vv) return;
      root.style.setProperty('--ck-vv-h', vv.height + 'px');
      root.style.setProperty('--ck-vv-top', vv.offsetTop + 'px');
    }
    /* iOS-PROOF SCROLL LOCK (Lucas, 20 Aug): overflow:hidden is ignored
       by iOS for touch — the FIXED-BODY pattern is the real lock (store
       scrollY, pin the body at -scrollY, restore on close: page lands
       exactly where it was, no jump to top). NOTE: the theme had NO
       existing fixed-body mechanism to reuse (QF sheet + menu drawer
       both use overflow:hidden — flagged, this is the first; named as
       THE lock owner so those surfaces can adopt it if ruled). <=767
       only, checked at open time; the class stays as the CSS hook +
       overflow belt for non-iOS. */
    var lockedY = null;
    function lockBody() {
      if (lockedY !== null) return;
      lockedY = window.scrollY || document.documentElement.scrollTop || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -lockedY + 'px';
      document.body.style.width = '100%';
      document.body.classList.add('ck-corner-open');
    }
    function unlockBody() {
      if (lockedY === null) {
        document.body.classList.remove('ck-corner-open');
        return;
      }
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('ck-corner-open');
      window.scrollTo(0, lockedY);
      lockedY = null;
    }
    function setOpen(open) {
      root.classList.toggle('is-open', open);
      panel.hidden = !open;
      launch.setAttribute('aria-expanded', String(open));
      if (open && window.matchMedia('(max-width: 767px)').matches) {
        lockBody();
      } else if (!open) {
        unlockBody();
      }
      if (vv) {
        if (open) {
          syncViewportVars();
          vv.addEventListener('resize', syncViewportVars);
          vv.addEventListener('scroll', syncViewportVars);
        } else {
          vv.removeEventListener('resize', syncViewportVars);
          vv.removeEventListener('scroll', syncViewportVars);
          root.style.removeProperty('--ck-vv-h');
          root.style.removeProperty('--ck-vv-top');
        }
      }
      if (open) {
        /* Lucas (20 Aug): focusing a text input raises the KEYBOARD on
           touch devices and can hide the form behind it — capability-
           gated like the tooltips ((hover:hover)+(pointer:fine), never
           width, so iPads behave like phones). Fine pointers keep the
           built behaviour (input focused); coarse pointers land on the
           dialog heading instead (tabindex=-1, ringless by the family
           rule) so screen readers still enter the dialog. preventScroll
           + explicit belt per the Safari register (#3). */
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          var first = form.querySelector('input:not([type="hidden"]), textarea');
          if (first) first.focus();
        } else {
          var title = panel.querySelector('#ck-corner-title');
          if (title) { try { title.focus({ preventScroll: true }); } catch (err) { title.focus(); } }
        }
      } else {
        launch.focus();
      }
    }

    launch.addEventListener('click', function (e) {
      e.preventDefault();
      setOpen(!root.classList.contains('is-open'));
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { setOpen(false); });
    if (backdrop) backdrop.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.keyCode === 27) && root.classList.contains('is-open')) setOpen(false);
    });

    /* footer dock (12 Aug): the launcher yields while the footer is in
       view (bottom-right footer content always reachable); the OPEN
       panel is user intent and never docks (CSS guards on .is-open). */
    var footer = document.querySelector('.ck-footer');
    if (footer && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        root.classList.toggle('is-footer-view', entries[0].isIntersecting);
      }, { threshold: 0 }).observe(footer);
    }

    /* vehicle prefill: same convention as the contact page's field */
    var veh = form.querySelector('[data-ck-corner-vehicle]');
    if (veh && !veh.value && window.clickableVehicleState && window.clickableVehicleState.getName) {
      var name = window.clickableVehicleState.getName();
      if (name) veh.value = name;
    }

    function showError() {
      if (errBox) errBox.hidden = false;
      var btn = form.querySelector('[data-ck-corner-send]');
      if (btn) btn.disabled = false;
    }

    /* SINGLE-OWNER SUBMISSION (13 Aug — race resolved by FIELD EVIDENCE
       + instrumented reproduction; supersedes the 12 Aug token-execute
       build, whose hcaptcha.execute returned instantly-empty and fired
       a token-less 400 — the false-error voice Lucas saw beside a
       DELIVERED ticket).

       THE RACE, measured: Shopify's captcha-bootstrap acts on the
       submit event INDEPENDENTLY of our preventDefault, executes
       hCaptcha out-of-band, and ~1.5s later calls NATIVE form.submit()
       with the token fields filled — that submission delivers.

       THE SHAPE: the BOOTSTRAP owns captcha + the submission decision;
       we stand down as submitter and SHIM form.submit — its final act
       flows through our fetch (FormData read at THAT moment carries
       the token), so exactly ONE submission happens, no navigation,
       and the panel paints TRUTH from the real response: 2xx -> sent,
       anything else -> the fail-closed error (message stays in the
       form). A delivered message can never paint the error state; a
       failed one can never paint success.

       FALLBACK: if the bootstrap never calls the shim (absent/broken —
       e.g. the CLI dev origin), a 6s timer posts the form as-is and
       the same truth-painting applies (200 on captcha-less surfaces,
       server-verdict error otherwise). One in-flight guard covers
       both triggers. */
    var pending = false;
    var fallbackTimer = null;

    function paintOutcome(r) {
      pending = false;
      if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
      if (!r.ok || (r.url && r.url.indexOf('/challenge') !== -1)) { showError(); return; }
      form.hidden = true;
      if (sent) {
        sent.hidden = false;
        sent.focus();
      }
    }

    function deliver() {
      if (!pending) return; /* only a user submit arms delivery */
      var fd = new FormData(form); /* read NOW — bootstrap-filled token rides along */
      fetch(form.getAttribute('action') || '/contact', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'text/html' }
      })
        .then(paintOutcome)
        .catch(function () { pending = false; showError(); });
    }

    /* the shim: the bootstrap's native form.submit() becomes our fetch */
    form.submit = function () { deliver(); };

    form.addEventListener('submit', function (e) {
      e.preventDefault(); /* the panel never navigates; the bootstrap ignores this and runs its captcha flow regardless (measured) */
      if (pending) return;
      if (!form.reportValidity()) return;
      if (errBox) errBox.hidden = true;
      var btn = form.querySelector('[data-ck-corner-send]');
      if (btn) btn.disabled = true;
      var page = form.querySelector('[data-ck-corner-page]');
      if (page) page.value = window.location.href;
      pending = true;
      fallbackTimer = setTimeout(deliver, 6000);
    });
  });
})();
